import {Edit} from "@refinedev/mui";
import {Box, Checkbox, FormControlLabel, TextField, Alert, MenuItem} from "@mui/material";
import {useForm} from "@refinedev/react-hook-form";
import {IResourceComponentsProps, useShow} from "@refinedev/core";
import {Controller} from "react-hook-form";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/auth-context";
import { canAccessUserData } from "../../utils/role-permissions";
import { PhoneNumberInput } from "../../components/common/PhoneInput";

export const UserEdit: React.FC<IResourceComponentsProps> = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user: currentUser } = useAuth();

    // Get user data to check access permissions
    const { queryResult } = useShow({
        resource: "users",
        id: id,
    });
    const { data, isLoading, error } = queryResult;
    const record = data?.data;

    const {
        saveButtonProps,
        register,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
        refineCore: { onFinish },
    } = useForm({
        refineCoreProps: {
            onMutationSuccess: () => {
                // User update successful
            },
            onMutationError: (error) => {
                console.error('Error during user update:', error);
            }
        }
    });

    // Update form values when record is loaded
    useEffect(() => {
        if (record) {
            const formData = {
                ...record,
                role: record.role || "USER", // Ensure role has a value
                password: "", // Clear password field
                confirmPassword: ""
            };

            // Reset form with the loaded data
            reset(formData);
        }
    }, [record, reset]);

    // Custom transform function to process form data before submission
    const transformFormData = (data: any) => {
        // Remove confirmPassword from data since it's only used for validation
        const { confirmPassword, ...submitData } = data;

        // Only include password if it's provided and not empty
        if (!submitData.password || submitData.password.trim() === '') {
            delete submitData.password;
        }

        return submitData;
    };

    // Company-based access control
    useEffect(() => {
        if (!isLoading && record && currentUser) {
            // Check if user can access the target user data
            if (!canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
                console.warn(`🚫 Access denied: User ${currentUser.id} tried to edit user ${record.id} from different company`);
                navigate('/users', {
                    replace: true
                });
                return;
            }
        }
    }, [record, currentUser, isLoading, navigate]);

    // Don't render if access should be denied
    if (!isLoading && record && currentUser &&
        !canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
        return null; // Component will unmount due to navigation
    }

    // Show error if user data failed to load
    if (error) {
        return (
            <Alert severity="error">
                Failed to load user information for editing
            </Alert>
        );
    }

    return (
        <Edit
            saveButtonProps={{
                ...saveButtonProps,
                onClick: (e) => {
                    const formData = watch();
                    const processedData = transformFormData(formData);

                    // Manually trigger the update with processed data
                    onFinish(processedData);
                }
            }}
        >
            <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column" }}
                autoComplete="off"
            >
                <TextField
                    {...register("id", {
                        required: "This field is required",
                        valueAsNumber: true,
                    })}
                    error={!!(errors as any)?.id}
                    helperText={(errors as any)?.id?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    label="Id"
                    name="id"
                    disabled
                />
                <TextField
                    {...register("email", {
                        required: "This field is required",
                    })}
                    error={!!(errors as any)?.email}
                    helperText={(errors as any)?.email?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="email"
                    label="Email"
                    name="email"
                />
                <TextField
                    {...register("fullname", {
                        required: "This field is required",
                    })}
                    error={!!(errors as any)?.fullname}
                    helperText={(errors as any)?.fullname?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Fullname"
                    name="fullname"
                />
                <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState }) => (
                        <PhoneNumberInput
                            {...field}
                            label="Phone"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            fullWidth
                            margin="normal"
                        />
                    )}
                />
                {/* Admin-only fields */}
                {currentUser?.role === 'ADMIN' && record && (
                    <>
                        <Controller
                            control={control}
                            name="role"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value || "USER"}
                                    select
                                    fullWidth
                                    label="Role"
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    error={!!(errors as any)?.role}
                                    helperText={(errors as any)?.role?.message}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                    }}
                                >
                                    <MenuItem value="USER">USER</MenuItem>
                                    <MenuItem value="MODERATOR">MODERATOR</MenuItem>
                                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                                </TextField>
                            )}
                        />
                        <TextField
                            {...register("password", {
                                minLength: {
                                    value: 8,
                                    message: "Password must be at least 8 characters long",
                                },
                            })}
                            error={!!(errors as any)?.password}
                            helperText={(errors as any)?.password?.message || "Leave empty to keep current password"}
                            margin="normal"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            label="New Password (Optional)"
                            name="password"
                        />
                        <TextField
                            {...register("confirmPassword", {
                                validate: (value) => {
                                    const password = watch("password");
                                    if (password && !value) {
                                        return "Please confirm your password";
                                    }
                                    if (value && value !== password) {
                                        return "Passwords do not match";
                                    }
                                    return true;
                                },
                            })}
                            error={!!(errors as any)?.confirmPassword}
                            helperText={(errors as any)?.confirmPassword?.message}
                            margin="normal"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            label="Confirm New Password"
                            name="confirmPassword"
                        />
                        <Controller
                            control={control}
                            name="hasBeenOnboarded"
                            defaultValue={false}
                            render={({ field }) => (
                                <FormControlLabel
                                    label="Has Been Onboarded"
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={!!field.value}
                                            onChange={(event) => {
                                                field.onChange(event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="isPremium"
                            defaultValue={false}
                            render={({ field }) => (
                                <FormControlLabel
                                    label="Premium"
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={!!field.value}
                                            onChange={(event) => {
                                                field.onChange(event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                    </>
                )}
            </Box>
        </Edit>
    );
};
